import { useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'

import { getLogoutUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import {
  Button,
  CheckIcon,
  CloseIcon,
  EyeIcon,
  EyeOffIcon,
  Flex,
  Form,
  IconButton,
  Input,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/gql'

import ConfirmPasswordModal from '../components/ConfirmPasswordModal'
import style from '../ProfileSettings.module.css'

export const ACCOUNT_UPDATE_PASSWORD = graphql(/* GraphQL */ `
  mutation AccountUpdatePassword($input: UpdatePasswordInput!) {
    updatePassword(input: $input) {
      ... on EmptySuccess {
        _empty
      }
      ... on InvalidInputError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

interface PasswordRule {
  rule: string
  isValid: (password: string) => boolean
}

// prettier-ignore
const PASSWORD_RULES: PasswordRule[] = [
  { rule: 'At least 8 characters long', isValid: (p: string) => p.length >= 8 },
  { rule: 'Contains at least 1 uppercase character', isValid: (p: string) => !!p.match(/[A-Z]/) },
  { rule: 'Contains at least 1 number or symbol', isValid: (p: string) => !!p.match(/[\d\W]/) },
]

const Password = (): JSX.Element => {
  const [updatePassword, { data, error, loading }] = useMutation(
    ACCOUNT_UPDATE_PASSWORD,
  )

  const [isMasked, setIsMasked] = useState(true)
  const [newPassword, setNewPassword] = useState('')
  const [currentPasswordError, setCurrentPasswordError] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const isValidPassword = PASSWORD_RULES.every((rule) =>
    rule.isValid(newPassword),
  )

  function handleSubmit(password: string): void {
    void updatePassword({
      variables: {
        input: {
          password,
          newPassword,
        },
      },
    })
  }

  function handleModalOpen(): void {
    setCurrentPasswordError('') // Clear any previous errors
    setIsModalOpen(true)
  }

  // Handle password update response
  useEffect(() => {
    if (!data && !error) return

    // Clear any existing error state
    setCurrentPasswordError('')

    const result = data?.updatePassword

    if (result?.__typename === 'EmptySuccess') {
      toast.success(
        'Your password has been updated! You will be redirected to the login page.',
        { duration: 5_000 },
      )
      window.location.assign(getLogoutUrl(window.location.href))
      return
    }

    if (result?.__typename === 'UnauthorizedError') {
      // Incorrect current password - keep dialog open and show inline error
      setCurrentPasswordError(result.message)
      return
    }

    // For all other errors, show toast and close modal
    const errorMessage =
      result?.__typename === 'InvalidInputError' ||
      result?.__typename === 'UnexpectedError'
        ? result.message
        : 'An error occurred while updating your password.'

    toast.error(errorMessage)
    setIsModalOpen(false)
  }, [data, error])

  return (
    <Form name="updatePasswordForm" className={style.fullWidth}>
      <Form.Field name="np" className={style.fullWidth}>
        <Form.Label htmlFor="np">Password</Form.Label>
        <Flex gap="xs" align="center" className={style.fullWidth}>
          <Form.Control>
            <Input
              ref={inputRef}
              className={style.flexInput}
              type={isMasked ? 'password' : 'text'}
              data-1p-ignore="true"
              name="np"
              placeholder="New password"
              onChange={(event): void => setNewPassword(event.target.value)}
              aria-invalid={newPassword && !isValidPassword ? true : undefined}
              aria-describedby="password-validation-messages"
              suffix={
                newPassword && (
                  <IconButton
                    label={isMasked ? 'Show password' : 'Hide password'}
                    size="small"
                    variant="tertiary"
                    onClick={(): void => {
                      setIsMasked(!isMasked)
                      inputRef.current?.focus()
                    }}
                  >
                    {isMasked ? (
                      <EyeIcon size="small" />
                    ) : (
                      <EyeOffIcon size="small" />
                    )}
                  </IconButton>
                )
              }
            />
          </Form.Control>
          <ConfirmPasswordModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            description="To confirm this password change, please enter your previous password."
            onConfirm={(password): void => {
              handleSubmit(password)
            }}
            error={currentPasswordError}
            isLoading={loading}
          >
            <Button
              size="medium"
              variant="secondary"
              disabled={!newPassword || !isValidPassword}
              className={style.fixedWidthButton}
              onClick={handleModalOpen}
            >
              Change Password
            </Button>
          </ConfirmPasswordModal>
        </Flex>
      </Form.Field>
      {newPassword && (
        <div
          className={style.passwordRules}
          id="password-validation-messages"
          aria-live="polite"
        >
          <Flex gap="xs" direction="column" align="start">
            {PASSWORD_RULES.map((rule) => (
              <Flex key={rule.rule} gap="sm" align="center" direction="row">
                {rule.isValid(newPassword) ? (
                  <CheckIcon size="small" color="success" />
                ) : (
                  <CloseIcon size="small" color="critical" />
                )}
                <Text
                  color={rule.isValid(newPassword) ? 'success' : 'critical'}
                >
                  {rule.rule}
                </Text>
              </Flex>
            ))}
          </Flex>
        </div>
      )}
    </Form>
  )
}

export default Password
